import { InjectionToken, Signal, signal } from '@angular/core';

/**
 * A flat key/value bag describing the current customer, used by the
 * shared cart/checkout/AddToCart components to pre-fill form fields.
 *
 * Apps consuming `@hypenation/tickets` can provide a real implementation
 * against `CHECKOUT_PROFILE_PROVIDER` (e.g. client-portal binds it to its
 * CustomerProfileService) without forcing this lib to depend on app
 * specific code.
 *
 * The named fields below are documentation hints — `AddToCartComponent`
 * looks them up by name (case-insensitive, with common variations like
 * `first_name`, `fname`, etc.). Any additional keys are forwarded as-is
 * via the index signature.
 */
export interface CheckoutProfile {
  /** Full display name (e.g. "John Doe"). */
  name?: string;
  /** First / given name. Set this when you can split the full name. */
  firstName?: string;
  /** Last / family name. */
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  [key: string]: unknown;
}

export interface CheckoutProfileProvider {
  /** Reactive accessor for the currently loaded profile, or null. */
  readonly profile: Signal<CheckoutProfile | null>;
}

/**
 * Default no-op provider used when no app-specific provider is bound.
 * Components that read this token will simply see `null`.
 */
export const NULL_CHECKOUT_PROFILE_PROVIDER: CheckoutProfileProvider = {
  profile: signal<CheckoutProfile | null>(null).asReadonly(),
};

export const CHECKOUT_PROFILE_PROVIDER = new InjectionToken<CheckoutProfileProvider>(
  'CHECKOUT_PROFILE_PROVIDER',
  {
    providedIn: 'root',
    factory: () => NULL_CHECKOUT_PROFILE_PROVIDER,
  }
);
